import React, { useEffect, useRef, useState } from 'react';
import { customTeam, findTeamById, searchTeams } from '../data/teams';
import TeamName from './TeamName';

const TeamPicker = ({ label, selectedId, selectedLabel, onSelect }) => {
  const selectedTeam = selectedId ? findTeamById(selectedId) : null;
  const displayLabel = selectedTeam?.label || selectedLabel;
  const displayLogo = selectedTeam?.logo || '';
  const displayLeague = selectedTeam?.league || '';
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const trimmedQuery = query.trim();
  const results = searchTeams(query);
  const hasExactLabel = results.some((team) => team.label.toLowerCase() === trimmedQuery.toLowerCase());
  const customOption = trimmedQuery && !hasExactLabel ? customTeam(trimmedQuery) : null;
  const options = customOption ? [...results, customOption] : results;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (team) => {
    onSelect(team);
    setQuery('');
    setIsOpen(false);
    setActiveIndex(0);
  };

  const handleKeyDown = (event) => {
    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setIsOpen(true);
      return;
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (!options.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + options.length) % options.length);
    } else if (event.key === 'Enter' && options[activeIndex]) {
      event.preventDefault();
      handleSelect(options[activeIndex]);
    }
  };

  return (
    <div className="block text-white" ref={containerRef}>
      <span className="block mb-1">{label}</span>
      {displayLabel && !isOpen ? (
        <button
          type="button"
          onClick={() => {
            setQuery(displayLabel);
            setIsOpen(true);
          }}
          className="mt-0 w-full p-2 rounded bg-gray-700 text-white text-left flex items-center justify-between"
        >
          <TeamName name={displayLabel} logo={displayLogo} />
          {displayLeague ? <span className="text-xs text-gray-400 ml-2">{displayLeague}</span> : null}
        </button>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
              setActiveIndex(0);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search Chiefs, Texas..."
            className="w-full p-2 rounded bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-accent"
          />
          {isOpen && (
            <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-gray-800 border border-gray-600 rounded shadow-lg">
              {trimmedQuery === '' ? (
                <li className="px-3 py-2 text-sm text-gray-400">Type a team, city, or abbreviation</li>
              ) : (
                options.map((team, index) => (
                  <li key={team.custom ? 'custom' : team.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(team)}
                      className={`w-full px-3 py-2 text-left flex items-center justify-between ${index === activeIndex ? 'bg-gray-600' : 'hover:bg-gray-700'}`}
                    >
                      {team.custom ? (
                        <span>Use "{team.label}" anyway</span>
                      ) : (
                        <TeamName name={team.label} logo={team.logo} />
                      )}
                      <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                        {team.custom ? 'No logo' : team.league}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamPicker;
